import { useEffect, useState } from 'react';
import Input from '../../components/Input';
import './update-user.css';
import { useNavigate, useParams } from 'react-router-dom';
import CloseButton from '../../components/CloseButton';
import RadioInput from '../../components/RadioInput';
import { put, get, remove, post } from '../../services/api';
import { useUI } from '../../UIContext';
import { handleErr } from '../../utils/functions';
import PhoneInput from '../../components/PhoneInput'
import ConfirmatioModal from '../../components/ConfirmationModal';
import CheckInput from '../../components/CheckInput';
import Permissions from '../../components/Permissions';

function UpdateUser() {
    const { id } = useParams();

    const individualPersonType = 'individual';
    const companyPersonType = 'company';
    const modularPlanType = 'modular';
    const hubPlanType = 'hub';
    const userTab = 0;
    const permissionTab = 1;

    const navigate = useNavigate();

    const { setMessage, setLoading } = useUI();
    const [user, setUser] = useState({});
    const [permissions, setPermissions] = useState([]);
    const [planType, setPlanType] = useState(modularPlanType);
    const [tabActive, setTabActive] = useState(userTab);
    const [submitAttempt, setSubmitAttempt] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [hasSavedProcess, setHasSavedProcess] = useState(false);
    const [resendingEmail, setResendingEmail] = useState(false);

    async function onResendConfirmationEmail() {
        setResendingEmail(true);
        await post(`/user/${user.id}/resend-confirmation-email`, {}, true, setMessage,
            () => {
                setMessage({ type: 'success', text: 'E-mail de confirmação reenviado.' });
            },
            (err) => {
                handleErr(setMessage, `Não foi possível reenviar o e-mail: ${err}`);
            },
            () => {
                setResendingEmail(false);
            }
        );
    }


    useEffect(() => {
        async function getUserPermissions(permissionsFound) {
            await get(`/permission/user/${id}`, true, setMessage,
                (response) => {
                    if (!response.data.modulesIds || response.data.modulesIds.length === 0) {
                        setPermissions(permissionsFound);
                        return;
                    }

                    setPermissions(
                        permissionsFound.map(el => {
                            const moduleExists = response.data.modulesIds.some(r => r === el.id);
                            if (!moduleExists)
                                return el;

                            return {
                                ...el,
                                checked: true
                            };
                        })
                    );
                },
                (err) => {
                    handleErr(setMessage, `Não foi possível carregar a lista de permissões: ${err}`);
                }
            )
        }

        async function getPermissionsList() {
            await get('/module/class/', true, setMessage,
                async (response) => {
                    await getUserPermissions(
                        response.data.map(el => {
                            return {
                                id: el.module.id,
                                name: el.module.name,
                                supplementary: el.module.supplementary,
                                checked: false
                            }
                        })
                    );
                },
                (err) => {
                    handleErr(setMessage, `Não foi possível carregar a lista de permissões: ${err}`);
                }
            )
        }

        async function getUserPlan() {
            await get(`/plan/user/${id}`, true, setMessage,
                (response) => {
                    if (!response?.data?.type)
                        return;

                    const planTypeResponse = response.data.type.toLowerCase();
                    if (planTypeResponse === hubPlanType) {
                        setPlanType(hubPlanType);
                        return;
                    }

                    setPlanType(modularPlanType);
                },
                () => {
                    setPlanType(modularPlanType);
                }
            )
        }
        
        async function getUserData() {
            setLoading(true);
            
            await get(`user/${id}`, true, setMessage,
                async (response) => {
                    setUser(response.data);
                    await getUserPlan();
                    await getPermissionsList();
                },
                (err) => {
                    handleErr(setMessage, `Não foi possível carregar o usuário: ${err}`);
                },
                () => {
                    setLoading(false);
                }
            )
        }
        
        getUserData();
    }, [id, setUser, setLoading, setMessage, setPermissions]);

    function onChangePersonType(e) {
        setUser({
            ...user,
            person: {
                ...user.person,
                type: e.target.value
            }
        });
    }

    function isCompanyPerson() {
        return user.person?.type?.toLowerCase() === companyPersonType;
    }

    function isNameValid() {
        return !!user.name && user.name.length >= 2 && user.name.length <= 56;
    }

    function isLastNameValid() {
        return !!user.lastName && user.lastName.length >= 2 && user.lastName.length <= 200;
    }

    function isEmailValid() {
        return !!user.email && user.email.length >= 4 && user.email.length <= 256;
    }

    function isDocumentNumberValid() {
        return !!user.person.documentNumber && user.person.documentNumber.length <= 60;
    }

    function clearScreen() {
        setSubmitAttempt(false);
    }

    async function onClickSave(e) {
        function showSuccess() {
            clearScreen();
            setHasSavedProcess(true);
            setMessage({ type: 'success', text: 'O usuário foi modificado.' });
        }

        function isSaveValid() {
            setSubmitAttempt(true);

            if (isCompanyPerson()) {
                return !!user.name && !!user.email && !!user.person.documentNumber;
            }

            return !!user.name && !!user.lastName && !!user.email && !!user.person.documentNumber;
        }

        e.preventDefault();

        if (!isSaveValid())
            return;

        setLoading(true);

        const data = {
            name: user.name,
            lastName: isCompanyPerson() ? null : user.lastName,
            email: user.email,
            active: user.active,
            person: {
                type: user.person.type,
                documentNumber: user.person.documentNumber,
                phone: user.person.phone
            }
        };

        await put(`user/${user.id}`, data, true, setMessage,
            async () => {
                await post('/plan/configure', {
                    type: planType,
                    userId: Number(id)
                }, true, setMessage,
                    async () => {
                        if (planType === hubPlanType) {
                            showSuccess();
                            return;
                        }

                        if (permissions.length === 0) {
                            showSuccess();
                            return;
                        }

                        const modulesChecked = permissions.filter(p => p.checked);

                        const data = {
                            modulesIds: modulesChecked.map(p => p.id)
                        };

                        await post(`permission/user/${id}`, data, true, setMessage,
                            () => {
                                showSuccess();
                            },
                            (err) => {
                                handleErr(setMessage, `Não foi possível incluir as permissões do usuário: ${err}`);
                            }
                        );
                    },
                    (err) => {
                        handleErr(setMessage, `Não foi possível configurar o plano do usuário: ${err}`);
                    },
                    () => {
                        setLoading(false);
                    }
                );
            },
            (err) => {
                handleErr(setMessage, `Não foi possível atualizar o usuário: ${err}`);
            },
            () => {
            }
        );
    }

    function onChangePlanType(e) {
        setPlanType(e.target.value);
    }

    function onClickRemove(e) {
        e.preventDefault();
        
        setShowDeleteConfirmation(true);
    }

    async function removeUser() {
        setShowDeleteConfirmation(false);
        setLoading(true);

        await remove(`user/${user.id}`, true, setMessage,
            () => {
                navigate('/users');
                setMessage({ type: 'success', text: 'O usuário foi removido.' });
            },
            (err) => {
                handleErr(setMessage, `Não foi possível remover o usuário: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    function onClickUserTab(e) {
        e.preventDefault();

        setTabActive(userTab);
    }

    function onClickPermissionTab(e) {
        e.preventDefault();

        setTabActive(permissionTab);
    }

    function onChangeModulePermission(permission) {
        setPermissions(prev =>
            prev.map(p => {
                if (p !== permission) return p;

                const newChecked = !p.checked;

                return {
                    ...p,
                    checked: newChecked
                };
            })
        );
    }

    return (
        <>
            <form className='create-user-form'>
                <header className='container-header'>
                    <h1>Editar Usuário</h1>

                    <CloseButton
                        onClick={() => navigate('/users')}
                        shouldConfirm={!hasSavedProcess}
                    />
                </header>

                <div className="tab-list">
                    <button
                        type='button'
                        onClick={onClickUserTab}
                        className={`tab-button ${tabActive === userTab ? 'active' : ''}`}>
                        Usuários
                    </button>
                    <button
                        type='button'
                        onClick={onClickPermissionTab}
                        className={`tab-button ${tabActive === permissionTab ? 'active' : ''}`}>
                        Planos
                    </button>
                </div>
                
                {tabActive === userTab && (
                    <>
                        <div className='person-type-container'>
                            <div style={{ display: 'flex', flex: 1 }}>
                                <RadioInput
                                    className='person-type-input'
                                    label='Pessoa Física'
                                    name='person-type'
                                    value={individualPersonType}
                                    checked={user.person?.type?.toLowerCase() === individualPersonType}
                                    onChange={onChangePersonType}
                                />
                                <RadioInput
                                    className='person-type-input'
                                    label='Empresa'
                                    name='person-type'
                                    value={companyPersonType}
                                    checked={user.person?.type?.toLowerCase() === companyPersonType}
                                    onChange={onChangePersonType}
                                />
                            </div>
                            <button
                                type="button"
                                className="resend-confirmation-btn blue-button"
                                style={{ float: 'right', marginLeft: 'auto' }}
                                disabled={user.emailConfirmed || resendingEmail}
                                onClick={onResendConfirmationEmail}
                            >
                                {user.emailConfirmed ? 'E-mail confirmado' : (resendingEmail ? 'Enviando...' : 'Reenviar e-mail de confirmação')}
                            </button>
                        </div>

                        <div className={`container-user-group-data ${isCompanyPerson() ? 'single-field' : ''}`}>
                            <span className='first-input-container'>
                                <Input
                                    className='commom-input glass-effect-15'
                                    type='text'
                                    value={user.name}
                                    onChange={(e) => setUser({ ...user, name: e.target.value })}
                                    onValidate={isNameValid}
                                    placeholder={isCompanyPerson() ? 'Denominação' : 'Nome'}
                                    shouldValidate={submitAttempt}
                                    maxLength={56}
                                    warningMsg='O nome deve conter ao menos 3 caracteres.'
                                />
                            </span>

                            {!isCompanyPerson() && (
                                <Input
                                    className='commom-input glass-effect-15'
                                    type='text'
                                    value={user.lastName}
                                    onChange={(e) => setUser({ ...user, lastName: e.target.value })}
                                    onValidate={isLastNameValid}
                                    placeholder='Sobrenome'
                                    shouldValidate={submitAttempt}
                                    maxLength={200}
                                    warningMsg='O sobrenome deve conter ao menos 3 caracteres.'
                                />
                            )}
                        </div>

                        <Input
                            className='commom-input glass-effect-15'
                            type='text'
                            value={user.email}
                            onChange={(e) => setUser({
                                ...user,
                                email: e.target.value
                            })}
                            onValidate={isEmailValid}
                            placeholder='E-mail'
                            shouldValidate={submitAttempt}
                            maxLength={256}
                            warningMsg='O e-mail deve ser informado.'
                        />

                        <div className='container-user-group-data'>
                            <span className='first-input-container'>
                                <Input
                                    className='commom-input glass-effect-15'
                                    type='text'
                                    value={user.person?.documentNumber}
                                    onChange={(e) => setUser({
                                        ...user,
                                        person: {
                                            ...user.person,
                                            documentNumber: e.target.value
                                        }
                                    })}
                                    onValidate={isDocumentNumberValid}
                                    placeholder='Número de documento'
                                    shouldValidate={submitAttempt}
                                    maxLength={60}
                                    warningMsg='O número de documento deve ser informado.'
                                    />
                            </span>

                            <span>
                                <PhoneInput
                                    className='commom-input glass-effect-15'
                                    placeholder='Contato Telefónico'
                                    value={user.person?.phone}
                                    onChange={(e) => setUser({
                                        ...user,
                                        person: {
                                            ...user.person,
                                            phone: e.target.value
                                        }
                                    })}
                                    />
                            </span>
                        </div>
                    </>
                )}

                {tabActive === permissionTab && (
                    <div className='permissions-tab'>
                        <div className='plan-type-container'>
                            <RadioInput
                                className='person-type-input'
                                label='Plano Modular'
                                name='plan-type'
                                value={modularPlanType}
                                checked={planType === modularPlanType}
                                onChange={onChangePlanType}
                            />

                            <RadioInput
                                className='person-type-input'
                                label='Plano HUB'
                                name='plan-type'
                                value={hubPlanType}
                                checked={planType === hubPlanType}
                                onChange={onChangePlanType}
                            />
                        </div>

                        {planType === hubPlanType ? (
                            <p className='hub-plan-text'>Neste plano o usuário tem acesso à todos os módulos</p>
                        ) : (
                            <Permissions
                                permissions={permissions}
                                onChangeModulePermission={onChangeModulePermission}
                            />
                        )}
                    </div>
                )}
                
                <div className='container-update-user'>
                    {tabActive === userTab && (
                        <span className='container-active-user'>
                            <CheckInput
                                title='Ativo'
                                value={user.active}
                                onChange={() => setUser({
                                    ...user,
                                    active: !user.active
                                })}
                            />
                        </span>
                    )}

                    <div className='container-active-user-buttons'>
                        <button
                            type='submit'
                            onClick={onClickRemove}
                            className='remove-user red-button'>
                            Excluir
                        </button>

                        <button
                            type='submit'
                            onClick={onClickSave}
                            className='blue-button zoom-effect'>
                            Salvar
                        </button>
                    </div>
                </div>
            </form>

            <ConfirmatioModal
                show={showDeleteConfirmation}
                title='Excluir o utilizador'
                description='Atenção! Esta opção é irreversível. Você realmente deseja excluir o utilizador?'
                onConfirm={() => removeUser()}
                onClose={() => setShowDeleteConfirmation(false)}
            />
        </>
    );
}

export default UpdateUser;